import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import {
  ArrowsClockwise, SignOut as SignOutIcon, SignIn as SignInIcon, X, CheckCircle,
  Camera, User, Trash, Warning, Pen, ArrowLeft,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONDITION_OPTIONS = [
  { value: 'neuf',         label: 'Neuf',         color: '#16A34A', bg: '#DCFCE7' },
  { value: 'bon',          label: 'Bon etat',     color: '#15803D', bg: '#E8F5E9' },
  { value: 'usage',        label: 'Usage',        color: '#CA8A04', bg: '#FEF9C3' },
  { value: 'reparation',   label: 'A reparer',    color: '#EA580C', bg: '#FFEDD5' },
  { value: 'hors_service', label: 'Hors service', color: '#DC2626', bg: '#FEE2E2' },
];

// -----------------------------------------------------------------------------
// QR Scanner sub-component
// -----------------------------------------------------------------------------
const QrScanner = ({ active, onResult }) => {
  const ref = useRef(null);
  const scannerRef = useRef(null);
  const isStartedRef = useRef(false);
  const lastTextRef = useRef(null);
  const lastTimeRef = useRef(0);

  useEffect(() => {
    if (!active) return;
    if (!ref.current) return;
    const elemId = `qr-reader-${Math.random().toString(36).slice(2, 8)}`;
    ref.current.id = elemId;
    const scanner = new Html5Qrcode(elemId, { verbose: false });
    scannerRef.current = scanner;
    let cancelled = false;
    const start = async () => {
      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            // De-bounce identical scans within 1.2s
            const now = Date.now();
            if (lastTextRef.current === decodedText && now - lastTimeRef.current < 1200) return;
            lastTextRef.current = decodedText;
            lastTimeRef.current = now;
            onResult(decodedText);
          },
          () => {}
        );
        if (!cancelled) isStartedRef.current = true;
      } catch (err) {
        console.warn('QR scanner start failed', err);
        toast.error("Impossible d'acceder a la camera");
      }
    };
    start();
    return () => {
      cancelled = true;
      if (scannerRef.current && isStartedRef.current) {
        scannerRef.current.stop().then(() => {
          scannerRef.current?.clear();
          isStartedRef.current = false;
        }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!active) return null;
  return (
    <div data-testid="qr-scanner-container" style={{ border: '2px solid #002FA7', borderRadius: 12, overflow: 'hidden', margin: '12px 0', background: 'black' }}>
      <div ref={ref} style={{ width: '100%', maxWidth: 420, margin: '0 auto' }} />
    </div>
  );
};

// -----------------------------------------------------------------------------
// Operator badge
// -----------------------------------------------------------------------------
const OperatorBadge = ({ operator, onReset }) => (
  <div data-testid="operator-badge" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 12, background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
    {operator.photo_url ? (
      <img src={`${API_URL}${operator.photo_url}`} alt={operator.name} style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', border: '2px solid #002FA7' }} />
    ) : (
      <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#002FA7', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
        {(operator.name || '?').slice(0, 1).toUpperCase()}
      </div>
    )}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#002FA7' }}>{operator.name}</div>
      <div style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{operator.job || operator.role}</div>
    </div>
    <button onClick={onReset} className="btn btn-secondary btn-sm" data-testid="op-reset" title="Changer d'operateur">
      <ArrowsClockwise size={14} />
    </button>
  </div>
);

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
const ScanMaterielPage = () => {
  const { user } = useAuth();
  const [operator, setOperator] = useState(null);
  const [opMode, setOpMode] = useState('scan'); // 'scan' | 'self'
  const [sessionType, setSessionType] = useState(null); // 'sortie' | 'retour'
  const [scanning, setScanning] = useState(false);
  const [items, setItems] = useState([]); // {equipment, condition, note}
  const [showSignature, setShowSignature] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doneResult, setDoneResult] = useState(null);
  const sigRef = useRef(null);

  // Auto-identify the logged-in user as operator if they are an employee
  useEffect(() => {
    if (!operator && user && user.role === 'employee') {
      setOperator({ id: user.id, name: user.name, role: user.role, photo_url: user.photo_url, job: user.job_title || '' });
    }
  }, [user, operator]);

  const handleIdentifyScan = async (text) => {
    try {
      const res = await axios.post(`${API_URL}/api/equipment/scan/identify`, { qr: text }, { withCredentials: true });
      setOperator(res.data);
      toast.success(`Bonjour ${res.data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'QR salarie invalide');
    }
  };

  const handleEquipmentScan = async (text) => {
    try {
      const res = await axios.post(`${API_URL}/api/equipment/scan/resolve-equipment`, { qr: text }, { withCredentials: true });
      // Avoid duplicates in the current basket
      if (items.find(i => i.equipment.id === res.data.id)) {
        toast.info(`${res.data.name} deja scanne`);
        return;
      }
      const defaultCondition = res.data.condition || 'bon';
      setItems(prev => [...prev, { equipment: res.data, condition: defaultCondition, note: '' }]);
      toast.success(`✓ ${res.data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'QR materiel invalide');
    }
  };

  const setItemCondition = (idx, condition) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, condition } : it));
  };
  const setItemNote = (idx, note) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, note } : it));
  };
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const startSession = (type) => {
    setSessionType(type);
    setItems([]);
    setScanning(true);
  };
  const resetAll = () => {
    setSessionType(null);
    setItems([]);
    setScanning(false);
    setShowSignature(false);
    setDoneResult(null);
  };

  const submit = useCallback(async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Signature requise pour valider');
      return;
    }
    setSubmitting(true);
    try {
      const sigData = sigRef.current.getTrimmedCanvas().toDataURL('image/png');
      const payload = {
        type: sessionType,
        operator_id: operator.id,
        items: items.map(it => ({
          equipment_id: it.equipment.id,
          condition: it.condition,
          note: it.note,
        })),
        signature_b64: sigData,
      };
      const res = await axios.post(`${API_URL}/api/equipment/scan/session`, payload, { withCredentials: true });
      setDoneResult(res.data);
      toast.success(`Session ${sessionType} confirmee (${res.data.processed} materiel(s))`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de validation');
    } finally {
      setSubmitting(false);
    }
  }, [items, operator, sessionType]);

  // ---------- Done / Recap screen ----------
  if (doneResult) {
    return (
      <div data-testid="scan-done" style={{ maxWidth: 640, margin: '0 auto', padding: '20px 16px' }}>
        <div style={{ background: 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)', color: 'white', padding: '24px 20px', borderRadius: 12, textAlign: 'center', marginBottom: 16 }}>
          <CheckCircle size={48} weight="bold" />
          <h2 style={{ margin: '8px 0 4px', fontSize: '1.3rem' }}>Session {doneResult.type === 'sortie' ? 'Sortie' : 'Retour'} validee</h2>
          <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.95 }}>{doneResult.processed} materiel(s) traite(s) - signe par {doneResult.operator_name}</p>
        </div>
        <button className="btn btn-primary" onClick={resetAll} data-testid="scan-new-session" style={{ width: '100%' }}>
          Nouvelle session
        </button>
      </div>
    );
  }

  // ---------- Step 1 : Operator identification ----------
  if (!operator) {
    return (
      <div data-testid="scan-step-operator" style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#002FA7' }}>
          <User size={22} weight="bold" /> Identification
        </h2>
        <p style={{ color: '#5E5F66', fontSize: '0.92rem' }}>Scannez votre carte salarie ou identifiez-vous manuellement.</p>
        {opMode === 'scan' ? (
          <>
            <QrScanner active onResult={handleIdentifyScan} />
            <button onClick={() => setOpMode('self')} className="btn btn-secondary" data-testid="op-mode-self" style={{ width: '100%', marginTop: 8 }}>
              <User size={14} /> Je suis deja connecte
            </button>
          </>
        ) : (
          <div style={{ padding: 16, border: '1px solid #E2E2E6', borderRadius: 8 }}>
            <p style={{ marginTop: 0 }}>Identifiez-vous avec votre compte BATI-BOYS</p>
            <button className="btn btn-primary" onClick={() => {
              if (!user) { toast.error('Vous devez vous connecter'); return; }
              setOperator({ id: user.id, name: user.name, role: user.role, photo_url: user.photo_url, job: user.job_title || '' });
            }} data-testid="op-use-current" style={{ width: '100%' }}>
              Continuer en tant que {user?.name || '...'}
            </button>
            <button onClick={() => setOpMode('scan')} className="btn btn-secondary" style={{ width: '100%', marginTop: 8 }}>
              <Camera size={14} /> Scanner une carte
            </button>
          </div>
        )}
      </div>
    );
  }

  // ---------- Step 2 : Choose session type ----------
  if (!sessionType) {
    return (
      <div data-testid="scan-step-type" style={{ maxWidth: 520, margin: '0 auto', padding: '20px 16px' }}>
        <h2 style={{ color: '#002FA7' }}>Type d'operation</h2>
        <OperatorBadge operator={operator} onReset={() => setOperator(null)} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 20 }}>
          <button onClick={() => startSession('sortie')} data-testid="type-sortie"
            style={{ padding: '28px 14px', border: '2px solid #002FA7', borderRadius: 14, background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <SignOutIcon size={36} weight="bold" color="#002FA7" />
            <strong style={{ color: '#002FA7', fontSize: '1.05rem' }}>Sortie</strong>
            <span style={{ fontSize: '0.78rem', color: '#5E5F66', textAlign: 'center' }}>Je prends du materiel</span>
          </button>
          <button onClick={() => startSession('retour')} data-testid="type-retour"
            style={{ padding: '28px 14px', border: '2px solid #16A34A', borderRadius: 14, background: 'linear-gradient(135deg, #ECFDF5, #DCFCE7)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <SignInIcon size={36} weight="bold" color="#16A34A" />
            <strong style={{ color: '#16A34A', fontSize: '1.05rem' }}>Retour</strong>
            <span style={{ fontSize: '0.78rem', color: '#5E5F66', textAlign: 'center' }}>Je rends du materiel</span>
          </button>
        </div>
      </div>
    );
  }

  // ---------- Step 3 : Signature ----------
  if (showSignature) {
    return (
      <div data-testid="scan-step-signature" style={{ maxWidth: 640, margin: '0 auto', padding: '20px 16px' }}>
        <button onClick={() => setShowSignature(false)} className="btn btn-secondary btn-sm" style={{ marginBottom: 12 }}>
          <ArrowLeft size={14} /> Retour
        </button>
        <h2 style={{ color: '#002FA7' }}>Recap & signature</h2>
        <OperatorBadge operator={operator} onReset={() => setOperator(null)} />
        <div style={{ marginTop: 14, padding: '10px 14px', background: '#F4F5F7', borderRadius: 8 }}>
          <p style={{ margin: 0, fontSize: '0.85rem' }}>
            <strong>{sessionType === 'sortie' ? 'Sortie' : 'Retour'}</strong> de {items.length} materiel(s)
          </p>
        </div>
        <ul data-testid="recap-list" style={{ listStyle: 'none', padding: 0, margin: '10px 0', maxHeight: 180, overflow: 'auto', border: '1px solid #E2E2E6', borderRadius: 8 }}>
          {items.map((it, idx) => {
            const cond = CONDITION_OPTIONS.find(c => c.value === it.condition);
            return (
              <li key={it.equipment.id} style={{ padding: '8px 12px', borderBottom: idx < items.length - 1 ? '1px solid #F4F5F7' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.88rem' }}>{it.equipment.name}</span>
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, background: cond?.bg, color: cond?.color }}>{cond?.label}</span>
              </li>
            );
          })}
        </ul>
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <Pen size={15} /> Signez ci-dessous
          </label>
          <div style={{ border: '2px dashed #002FA7', borderRadius: 8, background: 'white', touchAction: 'none' }} data-testid="signature-pad-wrap">
            <SignatureCanvas
              ref={sigRef}
              penColor="#0A0B10"
              canvasProps={{ width: 600, height: 200, style: { width: '100%', height: 200, display: 'block' }, 'data-testid': 'signature-canvas' }}
            />
          </div>
          <button onClick={() => sigRef.current?.clear()} className="btn btn-secondary btn-sm" data-testid="signature-clear" style={{ marginTop: 6 }}>Effacer</button>
        </div>
        <button onClick={submit} disabled={submitting} className="btn btn-primary" data-testid="scan-submit"
          style={{ width: '100%', marginTop: 16, padding: '14px', fontSize: '1rem', fontWeight: 700 }}>
          {submitting ? 'Envoi...' : `Valider la session ${sessionType}`}
        </button>
      </div>
    );
  }

  // ---------- Step 3 : Scan items + state ----------
  return (
    <div data-testid="scan-step-items" style={{ maxWidth: 640, margin: '0 auto', padding: '20px 16px' }}>
      <button onClick={() => setSessionType(null)} className="btn btn-secondary btn-sm" style={{ marginBottom: 12 }}>
        <ArrowLeft size={14} /> Changer de type
      </button>
      <h2 style={{ color: sessionType === 'sortie' ? '#002FA7' : '#16A34A', display: 'flex', alignItems: 'center', gap: 8 }}>
        {sessionType === 'sortie' ? <SignOutIcon size={22} weight="bold" /> : <SignInIcon size={22} weight="bold" />}
        Scanner {sessionType === 'sortie' ? 'les sorties' : 'les retours'}
      </h2>
      <OperatorBadge operator={operator} onReset={() => setOperator(null)} />
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => setScanning(s => !s)} className="btn btn-primary btn-sm" data-testid="scan-toggle">
          <Camera size={14} /> {scanning ? 'Mettre en pause' : 'Reprendre le scan'}
        </button>
        <span style={{ fontSize: '0.85rem', color: '#5E5F66' }}>{items.length} item(s) dans le panier</span>
      </div>
      <QrScanner active={scanning} onResult={handleEquipmentScan} />

      {/* Items list */}
      <div data-testid="scan-basket">
        {items.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#9E9E9E', background: '#F4F5F7', borderRadius: 8, marginTop: 8 }}>
            <Camera size={28} style={{ opacity: 0.5 }} />
            <p style={{ margin: '8px 0 0', fontSize: '0.85rem' }}>Aucun materiel scanne pour l'instant.</p>
          </div>
        ) : (
          items.map((it, idx) => (
            <div key={it.equipment.id} data-testid={`scan-item-${it.equipment.id}`} style={{ background: 'white', border: '1px solid #E2E2E6', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                {it.equipment.photo_url ? (
                  <img src={`${API_URL}${it.equipment.photo_url}`} alt={it.equipment.name} style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : null}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{it.equipment.name}</div>
                  <div style={{ fontSize: '0.74rem', color: '#5E5F66' }}>{it.equipment.brand} - {it.equipment.category}</div>
                </div>
                <button onClick={() => removeItem(idx)} className="btn btn-danger btn-sm" data-testid={`scan-item-remove-${it.equipment.id}`}><Trash size={12} /></button>
              </div>
              {/* Condition buttons */}
              {sessionType === 'retour' && (
                <>
                  <div style={{ fontSize: '0.7rem', color: '#5E5F66', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Etat au retour</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {CONDITION_OPTIONS.map(c => (
                      <button key={c.value} onClick={() => setItemCondition(idx, c.value)}
                        data-testid={`scan-condition-${c.value}-${it.equipment.id}`}
                        style={{
                          flex: '1 1 90px', padding: '6px 8px', border: `2px solid ${it.condition === c.value ? c.color : '#E2E2E6'}`,
                          borderRadius: 6, background: it.condition === c.value ? c.bg : 'white',
                          color: it.condition === c.value ? c.color : '#5E5F66',
                          fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer',
                        }}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                  {(it.condition === 'reparation' || it.condition === 'hors_service') && (
                    <textarea
                      placeholder="Decrivez brievement le probleme..."
                      value={it.note}
                      onChange={(e) => setItemNote(idx, e.target.value)}
                      data-testid={`scan-note-${it.equipment.id}`}
                      rows={2}
                      style={{ width: '100%', padding: '6px 8px', border: '1px solid #FED7AA', borderRadius: 6, marginTop: 6, fontSize: '0.85rem', fontFamily: 'inherit', background: '#FFF7ED' }}
                    />
                  )}
                </>
              )}
              {sessionType === 'sortie' && (
                <div style={{ fontSize: '0.74rem', color: '#5E5F66' }}>
                  <Warning size={11} style={{ verticalAlign: 'middle' }} /> Etat initial : <strong>{(CONDITION_OPTIONS.find(c => c.value === it.condition) || {}).label}</strong>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <button
        onClick={() => { setScanning(false); setShowSignature(true); }}
        disabled={items.length === 0}
        className="btn btn-primary"
        data-testid="scan-finish"
        style={{ width: '100%', padding: '12px', marginTop: 12, fontSize: '0.95rem', fontWeight: 700 }}
      >
        Terminer & signer ({items.length})
      </button>
    </div>
  );
};

export default ScanMaterielPage;
